import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { MobileContextService, ContextoPWA } from '../core/context/services/mobile-context.service';
import { PwaInitializationService } from '@infrastructure/pwa/services/pwa-initialization.service';
import { StandaloneAuthService } from '@core/auth/services/standalone-auth.service';
import { SessionKeepAliveService } from '@core/auth/services/session-keep-alive.service';
import { TokenMonitorService } from '@core/auth/services/token-monitor.service';
import { ThemeService } from '@app/core/services/theme.service';
import { AccessibilityService } from '@app/core/services/accessibility.service';
import { SettingsModalComponent } from '@infrastructure/ui/components/modals/settings-modal/settings-modal.component';
import { UserService, USER_STORAGE_KEY } from '@core/auth/services/user.service';
import { TokenService } from '@core/auth/services/token.service';
import { ItemEducacionalColorService } from '@core/context/services/item-educacional-color.service';
import { PlanoEnsinoService } from '@features/plano-ensino/services/plano-ensino.service';
import { environment } from '@environments/environment';
import { STORAGE_KEYS, LEGACY_STORAGE_KEYS } from '@shared/constants';

@Component({
  selector: 'app-pwa-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  title = 'Professores PWA';
  contexto: ContextoPWA | null = null;
  menuAberto = false;
  showTokenExpiredModal = false;
  contextoExpandido = false;
  userProfileOpen = false;
  contextSelectorOpen = false;
  semPermissaoOpen = false;
  isInitializing = true;
  planoEnsinoDisponivel = false;

  // Listeners de visibilitychange do ngOnInit, guardados para remoção no ngOnDestroy
  // (evita acumular listeners no document entre specs de teste).
  private onReturnFromCCTListener: (() => void) | null = null;
  private cctFallbackTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private onVisibilityChangeListener: (() => void) | null = null;

  @ViewChild('settingsModal') settingsModal!: SettingsModalComponent;

  constructor(
    private contextService: MobileContextService,
    private pwaInitService: PwaInitializationService,
    private standaloneAuth: StandaloneAuthService,
    private sessionKeepAlive: SessionKeepAliveService,
    private tokenMonitor: TokenMonitorService,
    private themeService: ThemeService,
    private accessibilityService: AccessibilityService,
    public userService: UserService,
    private tokenService: TokenService,
    private colorService: ItemEducacionalColorService,
    private planoEnsinoService: PlanoEnsinoService,
    private router: Router,
    private swUpdate: SwUpdate
  ) {
    // Força ativação imediata do novo SW: sem isso, o antigo continua servindo
    // cache de API até todos os tabs fecharem, o que nunca acontece no TWA.
    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')
      ).subscribe(() => {
        this.swUpdate.activateUpdate().then(() => window.location.reload());
      });
      this.swUpdate.checkForUpdate();
    }
  }

  async ngOnInit() {
    const searchParams = new URLSearchParams(window.location.search);

    if (window.location.pathname.includes('auth-callback.html')) {
      return;
    }

    const oauthCode = searchParams.get('code');
    const oauthState = searchParams.get('state') || '';
    if (oauthCode) {
      try {
        await this.standaloneAuth.handleCallback(oauthCode, oauthState);
      } catch {
        return;
      }
    }

    // false = redirecionou para login, paramos aqui
    const initialized = await this.pwaInitService.checkAndInitialize();
    if (!initialized) {

      // No TWA, window.location.href = '/?pwa_auth=true' abre um Chrome Custom Tab e o
      // WebView fica congelado; ao fechar o CCT, este listener recarrega a página para
      // que checkAndInitialize() processe o novo token.
      const onReturnFromCCT = () => {
        if (document.visibilityState === 'visible') {
          document.removeEventListener('visibilitychange', onReturnFromCCT);
          this.onReturnFromCCTListener = null;
          window.location.reload();
        }
      };
      this.onReturnFromCCTListener = onReturnFromCCT;
      document.addEventListener('visibilitychange', onReturnFromCCT);

      // Fallback: remove o listener após 5min (acomoda fluxos de 2FA) se o CCT nunca chegar a fechar.
      this.cctFallbackTimeoutId = setTimeout(() => {
        document.removeEventListener('visibilitychange', onReturnFromCCT);
        this.onReturnFromCCTListener = null;
        this.cctFallbackTimeoutId = null;
      }, 300000);

      return;
    }

    this.userService.fetchCurrentUser().subscribe();

    // TokenMonitorService reage a eventos `storage` cross-tab: se o bridge token some do
    // localStorage (só ocorre em clearLocalStorage() no logout real — renovação sempre
    // sobrescreve, nunca remove), desloga esta aba também.
    let previousMonitoredToken = this.tokenMonitor.getCurrentToken();
    this.tokenMonitor.token$.pipe(takeUntil(this.destroy$)).subscribe(token => {
      if (this.deveDeslogarPorPropagacaoCrossTab(previousMonitoredToken, token)) {
        window.location.href = '/?pwa_logout=true';
      }
      previousMonitoredToken = token;
    });

    // Listener para detectar quando o app volta do background (ex: Chrome Custom Tab fecha no TWA)
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Fallback de leitura na chave antiga (__pwa_bridge_token) por 1 release — TODO remover
        const bridgeToken = localStorage.getItem(STORAGE_KEYS.BRIDGE_TOKEN) ||
          localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_TOKEN);
        const standaloneToken = localStorage.getItem('__pwa_standalone_token');
        const standaloneExpiry = localStorage.getItem('__pwa_standalone_token_expiry');
        const token = bridgeToken || standaloneToken;
        const isAuthenticated = this.standaloneAuth.isAuthenticated();
        const isAngularJSRunning = !!(window as any).angular;

        if (token && !isAuthenticated) {

          if (isAngularJSRunning) {
            // AngularJS presente — reload para ele renovar o token via bridge
            setTimeout(() => { window.location.reload(); }, 100);
          } else {
            // NÃO limpar o bridge token aqui: pode ter sido escrito pelo AngularJS no CCT
            // e é necessário para a autenticação; reload deixa checkAndInitialize() processá-lo.
            setTimeout(() => { window.location.reload(); }, 100);
          }
        } else if (!token) {
          if (!isAngularJSRunning) {
            // Reload: o token pode ter sido escrito pelo CCT entre o check e esta ação
            setTimeout(() => { window.location.reload(); }, 100);
          }
        }
      }
    };
    this.onVisibilityChangeListener = onVisibilityChange;
    document.addEventListener('visibilitychange', onVisibilityChange);

    const bridgeToken = localStorage.getItem(STORAGE_KEYS.BRIDGE_TOKEN) ||
      localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_TOKEN);

    if (bridgeToken) {
      this.sessionKeepAlive.start();
    } else {
      const isAuth = this.standaloneAuth.isAuthenticated();
      
      if (isAuth) {
        this.sessionKeepAlive.start();
      }
    }

    window.addEventListener('abrirModalContexto', () => {
      this.abrirModalContexto();
    });

    window.addEventListener('abrirModalSemPermissao', () => {
      this.semPermissaoOpen = true;
    });

    this.contextService.contexto$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(contexto => {
      this.contexto = contexto;

      if (contexto && contexto.turma && contexto.turma.id) {
        this.contextSelectorOpen = false;

        this.planoEnsinoDisponivel = false;
        const identificador = environment.identificadorCamposAdicionaisPlanoEnsino;
        if (identificador) {
          this.planoEnsinoService
            .verificarDisponibilidade(contexto.turma.id, identificador)
            .pipe(takeUntil(this.destroy$))
            .subscribe((disponivel) => {
              this.planoEnsinoDisponivel = disponivel;
              if (!disponivel && this.router?.url?.includes('/plano-ensino')) {
                this.router.navigate(['/frequencia']);
              }
            });
        }

        // Só remove o overlay quando o contexto está enriquecido (com nome/descricao), não
        // apenas com ID — evita flash do spinner enquanto enriquecerContexto() ainda chama a API.
        if (contexto.turma.nome || contexto.turma.descricao) {
          this.isInitializing = false;
        }
      } else if (!this.pwaInitService.isLoadingContext) {
        this.isInitializing = false;
        this.abrirModalContexto();
      }
    });

    // Ao terminar o carregamento de contexto em background: se não achou nada, abre modal;
    // se achou só IDs, aguarda enriquecerContexto() emitir via contexto$ (subscriber acima).
    this.pwaInitService.contextLoadDone$.pipe(
      take(1),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      const ctx = this.contextService.getContextoAtual();
      if (!ctx || !ctx.turma || !ctx.turma.id) {
        this.isInitializing = false;
        this.abrirModalContexto();
      } else if (ctx.turma.nome || ctx.turma.descricao) {
        this.isInitializing = false;
      }
    });

    setTimeout(() => { this.isInitializing = false; }, 5000);

    this.standaloneAuth.token$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(token => {
      if (token) {
        const ctx = this.contextService.getContextoAtual();
        // Fallback de leitura na chave antiga (__pwa_bridge_contexto_completo) por 1 release — TODO remover
        const hasContextoCompleto = !!(localStorage.getItem(STORAGE_KEYS.BRIDGE_CONTEXTO_COMPLETO) ||
          localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_CONTEXTO_COMPLETO));
        if (!hasContextoCompleto && (!ctx || !ctx.turma || !ctx.turma.id)) {
          // Guard: só chama checkAndInitialize()/modal se o contexto NÃO está sendo carregado
          // em background (senão aguarda contexto$ tratar o estado) — evita double-call e
          // modal prematuro que aparece e some logo depois.
          if (!this.pwaInitService.isLoadingContext) {
            this.pwaInitService.checkAndInitialize().then(() => {
              if (this.pwaInitService.isLoadingContext) {
                this.pwaInitService.contextLoadDone$.pipe(
                  take(1),
                  takeUntil(this.destroy$)
                ).subscribe(() => {
                  const c = this.contextService.getContextoAtual();
                  if (!c || !c.turma || !c.turma.id) {
                    this.abrirModalContexto();
                  }
                });
              } else {
                const c = this.contextService.getContextoAtual();
                if (!c || !c.turma || !c.turma.id) {
                  this.abrirModalContexto();
                }
              }
            });
          }
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.sessionKeepAlive.stop();

    if (this.onReturnFromCCTListener) {
      document.removeEventListener('visibilitychange', this.onReturnFromCCTListener);
      this.onReturnFromCCTListener = null;
    }
    if (this.cctFallbackTimeoutId) {
      clearTimeout(this.cctFallbackTimeoutId);
      this.cctFallbackTimeoutId = null;
    }
    if (this.onVisibilityChangeListener) {
      document.removeEventListener('visibilitychange', this.onVisibilityChangeListener);
      this.onVisibilityChangeListener = null;
    }

    this.destroy$.next();
    this.destroy$.complete();
  }


  get isContextValid(): boolean {
    return !!(this.contexto && this.contexto.turma && this.contexto.turma.id);
  }

  get contextActionLabel(): string {
    return this.isContextValid ? 'Alterar contexto' : 'Selecionar contexto';
  }

  getComponentesLength(): number {
    return this.contexto?.componentes?.length || 0;
  }

  getPrimeiroComponente(): string {
    const primeiro = this.contexto?.componentes?.[0];
    return primeiro?.descricao || primeiro?.nome || '';
  }

  getNomeProfessor(): string {
    // Prioridade 1: API /user-accounts/v0.1/api/users/@me (fonte mais confiável)
    const nomeApi = this.userService.getNome();
    if (nomeApi) return nomeApi;

    try {
      // Prioridade 2: userInfo salvo pelo MobileContextService
      // Fallback de leitura na chave antiga (__pwa_bridge_contexto_completo) por 1 release — TODO remover
      const contextoCompleto = localStorage.getItem(STORAGE_KEYS.BRIDGE_CONTEXTO_COMPLETO) ||
        localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_CONTEXTO_COMPLETO);
      if (contextoCompleto) {
        const contexto = JSON.parse(contextoCompleto);
        if (contexto.userInfo) {
          const nome = contexto.userInfo.nome || contexto.userInfo.name || '';
          if (nome) return nome;
        }
      }
    } catch (e) {
      // Ignorar erro
    }

    return 'Usuário';
  }

  getUsername(): string {
    const usernameApi = this.userService.getUsername();
    if (usernameApi) return usernameApi;

    try {
      const contextoCompleto = localStorage.getItem(STORAGE_KEYS.BRIDGE_CONTEXTO_COMPLETO) ||
        localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_CONTEXTO_COMPLETO);
      if (contextoCompleto) {
        const contexto = JSON.parse(contextoCompleto);
        if (contexto.userInfo) {
          const username = contexto.userInfo.user || contexto.userInfo.username || '';
          if (username) return username;
        }
      }
    } catch (e) {
      // Ignorar erro
    }

    return '';
  }

  getUserPhoto(): string | null {
    const token = this.tokenService.getAccessToken();
    return this.userService.getUserPhotoUrl(token || '');
  }

  /** Cor do componente curricular, consistente entre contexto e quadro de horários. */
  getComponenteColor(componente: { id: number; color?: string }, index: number): string {
    return this.colorService.getColorFromClassName(componente.color) ?? this.colorService.getColorByIndex(index);
  }


  abrirModalContexto(): void {
    this.contextSelectorOpen = true;
  }

  onSemPermissaoTrocarContexto(): void {
    this.semPermissaoOpen = false;
    this.abrirModalContexto();
  }

  onContextChanged(novoContexto: ContextoPWA): void {
    this.menuAberto = false;
  }

  toggleMenu() {
    this.menuAberto = !this.menuAberto;
  }

  toggleContexto() {
    this.contextoExpandido = !this.contextoExpandido;
  }

  toggleUserProfile() {
    this.userProfileOpen = !this.userProfileOpen;
  }

  onOpenConta() {
    this.userProfileOpen = false;

    // Mesmo lugar de onde o AngularJS obtém a URL: suiteEnviropment['autorizacoes-ui'].stable.host
    try {
      const suiteEnviropment = (window as any).___bth?.envs?.suite;
      if (suiteEnviropment && suiteEnviropment['autorizacoes-ui'] && suiteEnviropment['autorizacoes-ui'].stable) {
        const urlUserAccounts = suiteEnviropment['autorizacoes-ui'].stable.host;
        const contaUrl = urlUserAccounts + '/access/dashboard.html';
        window.location.href = contaUrl;
        return;
      }
    } catch (error) {
      // Se não conseguir obter a URL, não navega
    }
  }

  /** True só quando o token existia e sumiu (logout real em outra aba, não renovação). */
  private deveDeslogarPorPropagacaoCrossTab(tokenAnterior: string | null, tokenAtual: string | null): boolean {
    return !!tokenAnterior && !tokenAtual;
  }

  onLogout() {
    this.userProfileOpen = false;
    // checkAndInitialize() usa essa flag para limpar tokens residuais do sessionStorage
    // e forçar re-login, mesmo que o AngularJS tenha re-autenticado via SSO no fluxo.
    sessionStorage.setItem('__pwa_post_logout', 'true');
    this.clearLocalStorage();
    window.location.href = '/?pwa_logout=true';
  }

  abrirModalConfiguracoes(): void {
    this.menuAberto = false;
    this.settingsModal.open();
  }

  private clearLocalStorage() {
    // Chave atual + antiga por 1 release — TODO remover
    localStorage.removeItem(STORAGE_KEYS.BRIDGE_TOKEN);
    localStorage.removeItem(LEGACY_STORAGE_KEYS.BRIDGE_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.BRIDGE_APP_CONTEXT);
    localStorage.removeItem(LEGACY_STORAGE_KEYS.BRIDGE_APP_CONTEXT);
    localStorage.removeItem(STORAGE_KEYS.BRIDGE_CONTEXTO_COMPLETO);
    localStorage.removeItem(LEGACY_STORAGE_KEYS.BRIDGE_CONTEXTO_COMPLETO);
    localStorage.removeItem(STORAGE_KEYS.BRIDGE_USER_ACCESS);
    localStorage.removeItem(LEGACY_STORAGE_KEYS.BRIDGE_USER_ACCESS);
    localStorage.removeItem(STORAGE_KEYS.BRIDGE_USER_ACCESS_CONTEXT);
    localStorage.removeItem(LEGACY_STORAGE_KEYS.BRIDGE_USER_ACCESS_CONTEXT);
    localStorage.removeItem(STORAGE_KEYS.BRIDGE_ACCESS_PAIR);
    localStorage.removeItem(LEGACY_STORAGE_KEYS.BRIDGE_ACCESS_PAIR);
    localStorage.removeItem('__pwa_standalone_token');
    localStorage.removeItem('__pwa_standalone_token_expiry');
    // Não emite null no BehaviorSubject aqui, pra evitar flash visual durante o redirect
    localStorage.removeItem(USER_STORAGE_KEY);
  }
}
